import { IconButton, MenuButton } from "@narmi/design_system";
import React from "react";
import { NavigationAnchor } from "./navigation/NavigationLink";
import styles from "./TopBar.module.css";

export interface TopBarProps {
  title: string | string[];
  subtitle?: string;
  action?: React.ReactNode;
  innerAction?: React.ReactNode;
  backIconOnClick?: () => void;
}

export const TopBar = ({ title, action, innerAction, backIconOnClick }: TopBarProps) => {
  return (
    <TopBar.Compact>
      {backIconOnClick ? <TopBar.BackButton onClick={backIconOnClick} /> : null}
      {Array.isArray(title) ? (
        title.map((value) => <TopBar.Breadcrumb key={value}>{value}</TopBar.Breadcrumb>)
      ) : (
        <TopBar.Breadcrumb>{title}</TopBar.Breadcrumb>
      )}
      {innerAction}
      <TopBar.Spacer />
      {action}
    </TopBar.Compact>
  );
};

function getPath(children: React.ReactNode): string {
  const segments: string[] = [];
  (function dfs(node: React.ReactNode) {
    if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
      if (node.type === TopBar.Breadcrumb) {
        if (node.props.children != null) {
          segments.push(React.Children.toArray(node.props.children).join(""));
        }
      } else if (node.props.children) {
        dfs(node.props.children);
      }
    } else if (Array.isArray(node)) {
      for (const child of node) {
        dfs(child);
      }
    }
  })(children);
  return segments.join(" > ");
}

function CompactTopBar({
  children,
  narrow = false,
}: {
  narrow?: boolean;
  children: React.ReactNode;
}) {
  const path = React.useMemo(() => getPath(children), [children]);

  // set the page title to the current route, which is the concatenation of the breadcrumb children
  React.useEffect(() => {
    const previousTitle = document.title;
    document.title = `${previousTitle} | ${path}`;
    return () => {
      document.title = previousTitle;
    };
  }, [path]);

  return (
    <div
      className={
        narrow ? `${styles.CompactTopBar} ${styles.CompactTopBarNarrow}` : styles.CompactTopBar
      }
    >
      {children}
    </div>
  );
}

function CompactTopBarBackButton({ onClick }: { onClick: () => void }) {
  return (
    <div className={styles.BackIcon}>
      <IconButton kind="plain" name="chevron-left" onClick={onClick} />
    </div>
  );
}

function CompactTopBarSpacer({ children }: React.PropsWithChildren) {
  return <div className={styles.CompactTopBarSpacer}>{children}</div>;
}

function CompactTopBarBreadcrumb({ to, children }: React.PropsWithChildren<{ to?: string }>) {
  if (!to) {
    return (
      <>
        <span data-testid="navigation-breadcrumb">{children}</span>
        <span className="narmi-icon-chevron-right"></span>
      </>
    );
  }
  return (
    <>
      <NavigationAnchor
        data-testid="navigation-breadcrumb"
        className={styles.CompactTopBarBreadcrumb}
        to={to}
      >
        {children}
      </NavigationAnchor>
      <span className="narmi-icon-chevron-right"></span>
    </>
  );
}

function CompactMenuButton({ children, label }: React.PropsWithChildren<{ label: string }>) {
  return (
    <div className={styles.MenuButton}>
      <MenuButton label={label}>{children}</MenuButton>
    </div>
  );
}

function Navigator({ onPrev, onNext }: { onPrev?: () => void; onNext?: () => void }) {
  return (
    <>
      <IconButton name="arrow-left" disabled={!onPrev} onClick={onPrev} />
      <IconButton name="arrow-right" disabled={!onNext} onClick={onNext} />
    </>
  );
}

TopBar.BackButton = CompactTopBarBackButton;
TopBar.Compact = CompactTopBar;
TopBar.Breadcrumb = CompactTopBarBreadcrumb;
TopBar.MenuButton = CompactMenuButton;
TopBar.Spacer = CompactTopBarSpacer;
TopBar.Navigator = Navigator;
